/**
 * Wishlist — header icon with a saved count, hearts on product cards, and a
 * slide-in panel listing what has been saved.
 *
 * Saves to localStorage rather than a customer account, so it works for
 * guests and needs no app. The trade-off is that the list lives in one
 * browser: it does not follow a shopper to another device, and clearing site
 * data clears it. A hosted wishlist app is the upgrade path if that matters.
 *
 * Nothing is injected server-side. The header markup comes from Dawn's own
 * header.liquid, and editing that file would put the theme out of step with
 * any future Dawn update, so the icon is inserted here instead. The feature
 * is JavaScript-driven anyway — with scripting off there is no wishlist to
 * show, which is the honest outcome rather than a dead heart.
 */
(() => {
  const KEY = 'cozycloud:wishlist';
  const EVENT = 'ct:wishlist:change';

  const HEART = `<svg class="ct-wish-heart" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
    <path d="M12 20.9 3.7 12.6a5.3 5.3 0 0 1 7.5-7.5l.8.8.8-.8a5.3 5.3 0 0 1 7.5 7.5Z"/>
  </svg>`;

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(value) ? value : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* private browsing or a full quota: the session still works, it just
         will not persist. Better than throwing mid-click. */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { list } }));
  };

  const saved = (handle) => read().some((item) => item.handle === handle);

  const toggle = (entry) => {
    const list = read();
    const at = list.findIndex((item) => item.handle === entry.handle);
    if (at > -1) list.splice(at, 1);
    else list.unshift(entry);
    write(list);
    return at === -1;
  };

  const remove = (handle) => write(read().filter((item) => item.handle !== handle));

  const handleFrom = (href) => {
    const part = (href || '').split('/products/')[1];
    return part ? part.split(/[?#]/)[0] : null;
  };

  /* ---- header icon ---- */

  const addHeaderIcon = (icons) => {
    if (icons.querySelector('.ct-wish-open')) return;

    const link = document.createElement('a');
    link.href = '#';
    link.className = 'header__icon header__icon--wishlist ct-wish-open link focus-inset';
    link.setAttribute('aria-label', 'Wishlist');
    link.innerHTML = `${HEART}<span class="ct-wish-count" aria-hidden="true">0</span>`;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      openPanel();
    });

    // Reference order is search, wishlist, account, cart.
    const before =
      icons.querySelector('shopify-account, .header__icon--account') ||
      icons.querySelector('.header__icon--cart');
    if (before) icons.insertBefore(link, before);
    else icons.appendChild(link);
  };

  /* ---- card hearts ---- */

  const decorateCards = (root) => {
    root.querySelectorAll('.card-wrapper').forEach((card) => {
      if (card.dataset.ctWish) return;

      const anchor = card.querySelector('.card__heading a');
      const inner = card.querySelector('.card__inner');
      const handle = handleFrom(anchor && anchor.getAttribute('href'));
      if (!anchor || !inner || !handle) return;

      const image = card.querySelector('.card__media img, .media img');
      const entry = {
        handle,
        title: anchor.textContent.trim(),
        url: anchor.getAttribute('href'),
        image: image ? image.currentSrc || image.src : '',
      };

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ct-wish-btn';
      button.dataset.handle = handle;
      button.innerHTML = HEART;
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle(entry);
      });

      inner.appendChild(button);
      card.dataset.ctWish = 'done';
    });
  };

  /* ---- panel ---- */

  let panel;

  const buildPanel = () => {
    panel = document.createElement('div');
    panel.className = 'ct-wish-panel';
    panel.hidden = true;
    panel.innerHTML = `
      <div class="ct-wish-panel__scrim" data-close></div>
      <aside class="ct-wish-panel__body" role="dialog" aria-modal="true" aria-label="Wishlist">
        <div class="ct-wish-panel__head">
          <h2 class="ct-wish-panel__title">Wishlist</h2>
          <button type="button" class="ct-wish-panel__close" aria-label="Close" data-close>&times;</button>
        </div>
        <p class="ct-wish-panel__empty">Nothing saved yet. Tap the heart on any product to keep it here.</p>
        <ul class="ct-wish-panel__list" role="list"></ul>
      </aside>`;

    panel.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]')) closePanel();
      const drop = e.target.closest('[data-remove]');
      if (drop) remove(drop.dataset.remove);
    });

    document.body.appendChild(panel);
  };

  const renderPanel = () => {
    if (!panel) return;
    const list = read();
    const ul = panel.querySelector('.ct-wish-panel__list');
    panel.querySelector('.ct-wish-panel__empty').hidden = list.length > 0;
    ul.innerHTML = list
      .map(
        (item) => `
        <li class="ct-wish-item">
          <a class="ct-wish-item__link" href="${item.url}">
            ${item.image ? `<img class="ct-wish-item__img" src="${item.image}" alt="" loading="lazy">` : ''}
            <span class="ct-wish-item__title">${item.title}</span>
          </a>
          <button type="button" class="ct-wish-item__remove" data-remove="${item.handle}"
            aria-label="Remove ${item.title} from wishlist">&times;</button>
        </li>`
      )
      .join('');
  };

  const openPanel = () => {
    if (!panel) return;
    renderPanel();
    panel.hidden = false;
    document.body.classList.add('ct-wish-lock');
    panel.querySelector('.ct-wish-panel__close').focus();
  };

  const closePanel = () => {
    if (!panel) return;
    panel.hidden = true;
    document.body.classList.remove('ct-wish-lock');
  };

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel && !panel.hidden) closePanel();
  });

  /* ---- keep every surface in step ---- */

  const sync = () => {
    const list = read();
    const count = list.length;

    document.querySelectorAll('.ct-wish-count').forEach((badge) => {
      badge.textContent = String(count);
    });
    document.querySelectorAll('.ct-wish-open').forEach((link) => {
      link.classList.toggle('is-filled', count > 0);
      link.setAttribute('aria-label', `Wishlist, ${count} saved`);
    });
    document.querySelectorAll('.ct-wish-btn').forEach((button) => {
      const on = saved(button.dataset.handle);
      button.classList.toggle('is-saved', on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
    });

    if (panel && !panel.hidden) renderPanel();
  };

  document.addEventListener(EVENT, sync);
  // Another tab saving something should show up here too.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) sync();
  });

  const boot = () => {
    document.querySelectorAll('.header__icons').forEach(addHeaderIcon);
    if (!panel) buildPanel();
    decorateCards(document);
    sync();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Dawn re-renders card grids for filters, pagination and quick add.
  const observer = new MutationObserver((records) => {
    let fresh = false;
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('.card-wrapper') || node.querySelector?.('.card-wrapper')) fresh = true;
      });
    });
    if (fresh) {
      decorateCards(document);
      sync();
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', boot);
})();
